import { getDatePlusMinus } from '@einhaus/meteoric-helpers';
/**
 * Canonical Project314 research-history calendar anchor.
 *
 * This is deliberately a calendar instant rather than a trading session. Each
 * product normalizes it through its explicit market calendar; US-equity
 * products therefore begin on 2020-08-03.
 */
export const OPTIONS_DATA_START_DATETIME = '2020-08-01 00:00:00';
export const buyTradeTypes = ['buy', 'buyToOpen', 'buyToCover', 'buyToClose', 'assignmentBuy'];
export const sellTradeTypes = ['sell', 'sellToClose', 'sellToOpen', 'shortSell'];
/**
 * Types that enter a trade, matching the backtest writer's `isOpeningTrade`. Brokerage and manual fills persist
 * `isOpeningTrade` from this set. `assignmentBuy` is settlement, never an entry.
 */
const OPENING_TRADE_TYPES = new Set(['buy', 'buyToOpen', 'sellToOpen', 'shortSell']);
/** Types that add quantity to a position: trade entries plus shares delivered by a short put assignment. */
const POSITION_INCREASE_TRADE_TYPES = new Set([...OPENING_TRADE_TYPES, 'assignmentBuy']);
export const isOpeningTradeType = (type) => OPENING_TRADE_TYPES.has(type);
/** Split transfers are excluded: they re-base existing quantity instead of adding to it. */
export const isPositionIncreaseTradeType = (type) => POSITION_INCREASE_TRADE_TYPES.has(type);
export const closeTradeTypes = ['sell', 'sellToClose', 'buyToClose', 'buyToCover', 'expiration', 'assignment'];
export const optionTradeTypes = ['buyToOpen', 'buyToClose', 'sellToOpen', 'sellToClose'];
export const STANDARD_DURATIONS = ['1m', '3m', '5m', '15m', '30m', '60m', '120m', '180m', '240m', '1d', '1w', '1mo'];
export const TRAILING_PERFORMANCE_DAYS = [7, 14, 30, 60, 90, 120, 180, 365, 545, 730];
export const CANDLESTICK_PATTERN_SUBTYPES = {
    ABANDONED_BABY_BEARISH: {
        id: 1,
        name: 'Abandoned Baby Bearish'
    },
    HAMMER_CANDLE: {
        id: 2,
        name: 'Hammer Candle'
    },
    INVERTED_HAMMER_CANDLE: {
        id: 3,
        name: 'Inverted Hammer Candle'
    },
    DOJI: {
        id: 4,
        name: 'Doji'
    },
    SPINNING_TOP_BEARISH: {
        id: 5,
        name: 'Spinning Top Bearish'
    },
    SPINNING_TOP_BULLISH: {
        id: 6,
        name: 'Spinning Top Bullish'
    },
    TWEEZER_TOP: {
        id: 7,
        name: 'Tweezer Top'
    },
    TWEEZER_BOTTOM: {
        id: 8,
        name: 'Tweezer Bottom'
    },
    DARK_CLOUD_COVER: {
        id: 9,
        name: 'Dark Cloud Cover'
    },
    BEARISH_ENGULFING: {
        id: 10,
        name: 'Bearish Engulfing'
    },
    BULLISH_ENGULFING: {
        id: 11,
        name: 'Bullish Engulfing'
    },
    BULLISH_HARAMI: {
        id: 12,
        name: 'Bullish Harami'
    },
    BEARISH_HARAMI: {
        id: 13,
        name: 'Bearish Harami'
    },
    PIERCING: {
        id: 14,
        name: 'Piercing'
    },
    DOJI_STAR: {
        id: 15,
        name: 'Doji Star'
    },
    BEARISH_HARAMI_CROSS: {
        id: 16,
        name: 'Bearish Harami Cross'
    },
    BULLISH_HARAMI_CROSS: {
        id: 17,
        name: 'Bullish Harami Cross'
    },
    BULLISH_KICKER: {
        id: 18,
        name: 'Bullish Kicker'
    },
    BEARISH_KICKER: {
        id: 19,
        name: 'Bearish Kicker'
    },
    THREE_INSIDE_UP: {
        id: 20,
        name: 'Three Inside Up'
    },
    THREE_INSIDE_DOWN: {
        id: 21,
        name: 'Three Inside Down'
    },
    THREE_BLACK_CROWS: {
        id: 22,
        name: 'Three Black Crows'
    },
    THREE_WHITE_SOLIDERS: {
        id: 23,
        name: 'Three White Soliders'
    },
    MORNING_STAR: {
        id: 24,
        name: 'Morning Star'
    },
    EVENING_STAR: {
        id: 25,
        name: 'Evening Star'
    },
    RISING_THREE_METHODS: {
        id: 26,
        name: 'Rising Three Methods'
    },
    FALLING_THREE_METHODS: {
        id: 27,
        name: 'Falling Three Methods'
    },
    THREE_LINE_STRIKE_BULLISH: {
        id: 28,
        name: 'Three Line Strike Bullish'
    },
    THREE_LINE_STRIKE_BEARISH: {
        id: 29,
        name: 'Three Line Strike Bearish'
    },
    ABANDONED_BABY_BULLISH: {
        id: 30,
        name: 'Abandoned Baby Bullish'
    },
    OUTSIDE_BAR: {
        id: 33,
        name: 'Outside Bar'
    },
    INSIDE_BAR: {
        id: 34,
        name: 'Inside Bar'
    },
    OUTSIDE_INSIDE_BAR: {
        id: 35,
        name: 'Outside Inside Bar'
    },
    INSIDE_OUTSIDE_BAR: {
        id: 36,
        name: 'Inside Outside Bar'
    },
    BULLISH_INSIDE_BAR_REVERSAL: {
        id: 37,
        name: 'Bullish Inside Bar Reversal'
    },
    BEARISH_INSIDE_BAR_REVERSAL: {
        id: 38,
        name: 'Bearish Inside Bar Reversal'
    },
    SHOOTING_STAR: {
        id: 39,
        name: 'Shooting Star'
    },
    HANGING_MAN: {
        id: 40,
        name: 'Hanging Man'
    },
    NIRVANA: {
        id: 41,
        name: 'Nirvana'
    },
    HOLY_GRAIL: {
        id: 42,
        name: 'Holy Grail'
    },
    GRAVESTONE_DOJI: {
        id: 43,
        name: 'Gravestone Doji'
    },
    DRAGONFLY_DOJI: {
        id: 44,
        name: 'Dragonfly Doji'
    },
    EVENING_STAR_WITH_DOJI: {
        id: 45,
        name: 'Evening Star With Doji'
    },
    MORNING_STAR_WITH_DOJI: {
        id: 46,
        name: 'Morning Star With Doji'
    }
};
export const CANDLESTICK_PATTERN_ID_MAP = Object.fromEntries(Object.entries(CANDLESTICK_PATTERN_SUBTYPES).map(([_, value]) => [value.id, value.name]));
export const DEFAULT_PORTFOLIO_BALANCE = 100_000;
export const DEFAULT_TRADE_SIZE = 10_000;
export const DEFAULT_RISK_CAPITAL_SIZE = 100_000;
export const OPTION_TRADING_FEE = 0.65;
export const DEFAULT_TRADE_SIZE_PERCENTAGE = 0.1;
export const DEFAULT_PRICE_TREND_LOOKBACK_BARS = [10, 20, 50, 100, 200, 365];
export const DEFAULT_LEVEL_PRICE_INTERACTION_LEVELS = ['vwap', 'sma-50', 'sma-200', 'ema-20', 'ema-50'];
export const DEFAULT_COMPARISON_LOOKBACK_BARS = [7, 14, 30, 60, 90];
/**
 * Research-window policy (walk-forward / out-of-sample strategy development).
 * See docs/plans/OUT_OF_SAMPLE_HOLDOUT_METHODOLOGY_PROPOSAL.md.
 *
 * getDevelopmentCutoffDateTime() is the rolling training-end boundary. Strategy-development
 * surfaces (experiments, parameter discovery, AERO, scorecard baselines) default their END
 * date to this so nothing is fit on post-cutoff data. Validation surfaces (portfolio/single
 * backtest, screener confirmation) intentionally run past it, up to the latest safe market
 * date (see getLatestSafeMarketDateTime).
 *
 * Keep this as a full datetime string so lexicographic comparisons against experiment
 * `YYYY-MM-DD HH:mm:ss` values match chronological ordering.
 */
export const DEFAULT_WALK_FORWARD_YEARS = 2;
/** Resolve at creation time; saved research windows must keep their persisted boundaries. */
export const getDevelopmentCutoffDateTime = (asOfDateTime) => {
    const normalized = asOfDateTime instanceof Date ? Math.floor(asOfDateTime.getTime() / 1_000) : asOfDateTime;
    if (typeof normalized === 'number' && !Number.isFinite(normalized))
        throw new Error('Invalid research-window as-of Date.');
    return getDatePlusMinus('-', DEFAULT_WALK_FORWARD_YEARS, 'year', 'ymdhms', 'start', normalized);
};
/**
 * Days subtracted from "now" to land on the latest safe (settled) market date. Recent bar/options
 * data settle with a lag, so walk-forward/backtest windows should end here rather than at the literal
 * current instant. Matches the established getDatePlusMinus('-', 2, 'day', ...) usage.
 */
export const WALK_FORWARD_SAFETY_BUFFER_DAYS = 2;
/**
 * The latest safe (settled) market datetime to use as the end of a walk-forward/backtest window:
 * end-of-day, WALK_FORWARD_SAFETY_BUFFER_DAYS days before now.
 */
export const getLatestSafeMarketDateTime = (returnFormat = 'ymdhms') => getDatePlusMinus('-', WALK_FORWARD_SAFETY_BUFFER_DAYS, 'day', returnFormat, 'end');
/**
 * Assemble the training (in-sample) and sealed walk-forward (out-of-sample) windows for a research epoch.
 * Candidate discovery, ranking, and selection must stay inside training. Only the committed candidate
 * may read the full walk-forward window. `trainingStart` is supplied by the caller (typically
 * OPTIONS_DATA_START_DATETIME) so this helper stays decoupled from repo-specific data-start constants.
 */
export const getResearchWindow = (trainingStart, asOfDateTime) => {
    const normalizedAsOfDateTime = (() => {
        if (!(asOfDateTime instanceof Date))
            return asOfDateTime;
        const timestampMs = asOfDateTime.getTime();
        if (!Number.isFinite(timestampMs))
            throw new Error('Invalid research-window as-of Date.');
        return Math.floor(timestampMs / 1_000);
    })();
    const trainingEnd = getDevelopmentCutoffDateTime(normalizedAsOfDateTime);
    const walkForwardEnd = getDatePlusMinus('-', WALK_FORWARD_SAFETY_BUFFER_DAYS, 'day', 'ymdhms', 'end', normalizedAsOfDateTime);
    return {
        trainingStart,
        trainingEnd,
        walkForwardStart: trainingEnd,
        walkForwardEnd
    };
};
/** Split a persisted evaluation window on calendar anniversaries, retaining a partial final year. */
export const getWalkForwardYearWindows = (startDateTime, endDateTime) => {
    if (!(startDateTime < endDateTime))
        throw new Error('Walk-forward start must precede its end.');
    const windows = [];
    let start = startDateTime;
    for (let yearNumber = 1; start < endDateTime; yearNumber += 1) {
        const anniversary = getDatePlusMinus('+', yearNumber, 'year', 'ymdhms', null, startDateTime);
        if (!(anniversary > start))
            throw new Error('Invalid walk-forward anniversary.');
        const end = anniversary < endDateTime ? anniversary : endDateTime;
        windows.push({ yearNumber, startDateTime: start, endDateTime: end });
        start = end;
    }
    return windows;
};
//# sourceMappingURL=constants.js.map